/**
 * Word/phrase translation popup — a minimal white speech bubble (iOS
 * SpeechBubblePopup parity) that floats ABOVE the tapped word/phrase with a
 * downward pointer, replacing the old fixed dark card.
 *
 * Modes:
 * - "word": the native translation + a known/learning checkmark-circle toggle.
 * - "sentence": the server phrase translation (`POST /auth/phrase`) + a bookmark
 *   save toggle (`PUT /auth/phrase/saved`). While the translation is in flight it
 *   shows the "Parroting… 🦜" state; saving pops a "You saved this phrase" toast.
 *
 * Positioning mirrors iOS SpeechBubblePopup.computeFrame: the bubble is centered
 * on the word's center-x, clamped to a 16px screen margin, while the pointer is
 * independently clamped within the bubble body (corner radius + tail). The
 * anchor (word/phrase rect) is published by the transcript via `popupAnchor$`.
 * Rendered through a portal so it isn't clipped by the video overlay; a
 * transparent backdrop closes it on outside tap (iOS has no × button).
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { observer } from "@legendapp/state/react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkles, Bookmark, CheckCircle2 } from "lucide-react";
import { colors } from "@/ui";
import { callWithAuth, postPhrase, putPhraseSaved } from "@/api/Api";
import type { UserVideoWord, PanelMode } from "../../types";
import {
  readWordOverride,
  selectedPhraseInfo$,
  popupAnchor$,
  savedPhraseToast$,
} from "../../state/videoState";
import { Explainer } from "../../features/Explainer";
import { useTrackFeedEvents } from "../../hooks/useTrackFeedEvents";

const TEXT_COLOR = "#1F1F1F";
const ICON_DARK = "#1F1F1F";
const ICON_SIZE = 22;

// iOS SpeechBubblePopup constants (px).
const MARGIN = 16; // min gap from the screen edges
const GAP = 8; // gap between the pointer tip and the word top
const CORNER = 18; // bubble corner radius
const TAIL_HALF = 7; // half the pointer base width
const POINTER_INSET = CORNER + TAIL_HALF; // pointer stays this far from each end
const TAIL_VISIBLE = 6; // how far the pointer shows below the body

type Anchor = { cx: number; top: number; left: number; right: number };

/**
 * White speech bubble, portaled to <body> and positioned above `anchor` with a
 * downward pointer. Measures itself, then applies the iOS clamp math.
 */
function FloatingBubble({
  anchor,
  onClose,
  children,
  showBackdrop = true,
}: {
  anchor: Anchor | null;
  onClose: () => void;
  children: React.ReactNode;
  /** The outside-tap-to-close backdrop. Suppressed while the Explainer sheet is
   *  open, or its body-portaled z-55 layer would block the (lower-stacked,
   *  overlay-nested) explainer from receiving taps. */
  showBackdrop?: boolean;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((prev) =>
        prev &&
        Math.abs(prev.w - r.width) < 0.5 &&
        Math.abs(prev.h - r.height) < 0.5
          ? prev
          : { w: r.width, h: r.height },
      );
    };
    measure();
    // Re-measure when the content resizes (e.g. the phrase translation loads).
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The anchor rect + window width are captured at tap time, so a viewport
  // change (rotation, window resize) while the bubble is open would leave it
  // mis-positioned. Just dismiss it — it's transient and the next tap re-anchors.
  // (window 'resize' covers rotation/resize but not the noisy mobile toolbar
  // show/hide, which only fires visualViewport resize.)
  useEffect(() => {
    window.addEventListener("resize", onClose);
    return () => window.removeEventListener("resize", onClose);
  }, [onClose]);

  // Dismiss on an outside press WITHOUT a blocking full-screen backdrop: a
  // backdrop swallowed scroll/touch so the transcript couldn't be scrolled while
  // a translation was open. Instead listen on the document and close on a press
  // that is neither inside the bubble NOR inside the transcript — so the user can
  // still scroll/tap the transcript with the bubble open, while a tap on the
  // video (etc.) dismisses it.
  useEffect(() => {
    if (!showBackdrop) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (bodyRef.current?.contains(t)) return;
      if (t.closest('[aria-label="Transcript"]')) return; // keep transcript live
      onClose();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [showBackdrop, onClose]);

  let bodyStyle: CSSProperties = {
    position: "fixed",
    left: 0,
    top: 0,
    visibility: "hidden",
  };
  let pointerLeft = "50%";
  // Constrain the bubble to the VIDEO column width (anchor.left/right), so on a
  // wide desktop it never spills past the narrow video into the black gutter.
  const maxBubbleW = anchor
    ? Math.max(120, anchor.right - anchor.left - 2 * MARGIN)
    : undefined;
  if (anchor && size) {
    const boundsLeft = anchor.left + MARGIN;
    const boundsRight = anchor.right - MARGIN;
    const w = size.w;
    // Center under the word, then clamp the bubble body within the video column.
    let bx = anchor.cx - w / 2;
    bx = Math.max(boundsLeft, Math.min(bx, boundsRight - w));
    // Pointer points back at the word, independently clamped within the body.
    let px = anchor.cx - bx;
    px = Math.max(POINTER_INSET, Math.min(px, w - POINTER_INSET));
    let by = anchor.top - GAP - TAIL_VISIBLE - size.h;
    by = Math.max(GAP, by); // never above the screen top
    bodyStyle = { position: "fixed", left: bx, top: by, visibility: "visible" };
    pointerLeft = `${px}px`;
  }

  return createPortal(
    <>
      {/* No full-screen backdrop — it blocked transcript scrolling. Outside-press
          dismissal is handled by the document listener above. */}
      <div
        ref={bodyRef}
        className="inline-flex items-center gap-2 bg-white"
        style={{
          ...bodyStyle,
          zIndex: 56,
          // Cap at the video column width (falls back to phone width before the
          // anchor's bounds are known) so it never overflows the video.
          maxWidth: maxBubbleW
            ? `${maxBubbleW}px`
            : "min(420px, calc(100vw - 32px))",
          borderRadius: CORNER,
          padding: "10px 13px",
          boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        <span
          aria-hidden
          className="absolute bg-white"
          style={{
            left: pointerLeft,
            bottom: -5,
            width: 12,
            height: 12,
            transform: "translateX(-50%) rotate(45deg)",
            borderRadius: 2,
            boxShadow: "2px 2px 4px rgba(0,0,0,0.05)",
          }}
        />
      </div>
    </>,
    document.body,
  );
}

interface BottomPanelProps {
  mode: PanelMode;
  /** The word being translated (required when mode === "word"). */
  word?: UserVideoWord | null;
  /** Word-by-word native gloss fallback (mode === "sentence"). */
  translation?: string;
  /** Lesson id — needed for phrase save tracking + explainer analytics. */
  lessonId: number;
  /** Chapter id — included in the iOS explainer analytics payload. */
  chapterId: number;
  /** Marks the selected word known/learning (mode === "word"). */
  onSave?: () => void;
  onClose: () => void;
}

const BottomPanel = observer(function BottomPanel({
  mode,
  word,
  translation,
  lessonId,
  chapterId,
  onSave,
  onClose,
}: BottomPanelProps) {
  const anchor = popupAnchor$.get();
  const [explainerOpen, setExplainerOpen] = useState(false);
  // AI explainer ("Explain" / Sparkles) gated behind a PostHog flag. Default OFF,
  // so the bubble stays minimal unless the flag is explicitly enabled.
  const aiExplainerEnabled =
    useFeatureFlagEnabled("ai_explainer_enabled") === true;
  const { trackWordMovedToKnown, trackWordMovedToLearning } =
    useTrackFeedEvents();

  const effectiveStatus =
    mode === "word" && word
      ? (readWordOverride(word.wordKey) ?? word.status)
      : "new";
  const isKnown = effectiveStatus === "known";

  const handleKnownToggle = () => {
    if (word) {
      const movingToKnown = !isKnown;
      const wordId = word.definition.wordKey;
      if (movingToKnown) trackWordMovedToKnown(wordId, word.displayText);
      else trackWordMovedToLearning(wordId, word.displayText);
    }
    onSave?.();
  };

  if (mode === "word" && word) {
    return (
      <FloatingBubble
        anchor={anchor}
        onClose={onClose}
        showBackdrop={!explainerOpen}
      >
        <span
          className="leading-snug min-w-0"
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: TEXT_COLOR,
            overflowWrap: "anywhere",
          }}
        >
          {word.definition.nativeWord}
        </span>
        <button
          type="button"
          aria-label={isKnown ? "Mark as learning" : "Mark as known"}
          className="shrink-0 flex items-center justify-center"
          style={{ padding: "0 2px" }}
          onClick={(e) => {
            e.stopPropagation();
            handleKnownToggle();
          }}
        >
          <CheckCircle2
            size={ICON_SIZE}
            color={isKnown ? "#fff" : ICON_DARK}
            fill={isKnown ? colors.green : "none"}
            strokeWidth={2}
          />
        </button>
        {aiExplainerEnabled && (
          <ExplainButton onClick={() => setExplainerOpen(true)} />
        )}
        {aiExplainerEnabled && explainerOpen && (
          <Explainer
            word={word.displayText}
            translation={word.definition.nativeWord}
            context={word.displayText}
            isPhrase={false}
            lessonId={lessonId}
            chapterId={chapterId}
            onClose={() => setExplainerOpen(false)}
          />
        )}
      </FloatingBubble>
    );
  }

  // Sentence (phrase) mode — server translation + save.
  return (
    <PhrasePanel
      anchor={anchor}
      lessonId={lessonId}
      chapterId={chapterId}
      fallbackGloss={translation}
      onClose={onClose}
      explainerOpen={explainerOpen}
      setExplainerOpen={setExplainerOpen}
      aiExplainerEnabled={aiExplainerEnabled}
    />
  );
});

function ExplainButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Explain"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="shrink-0 flex items-center justify-center"
      style={{ padding: "0 2px", color: colors.blue }}
    >
      <Sparkles className="w-5 h-5" />
    </button>
  );
}

/**
 * Phrase panel — fetches a fluent server translation via `POST /auth/phrase`,
 * shows the "Parroting…" state while loading, and exposes a bookmark/save toggle
 * (`PUT /auth/phrase/saved`) that pops a "saved" toast.
 */
const PhrasePanel = observer(function PhrasePanel({
  anchor,
  lessonId,
  chapterId,
  fallbackGloss,
  onClose,
  explainerOpen,
  setExplainerOpen,
  aiExplainerEnabled,
}: {
  anchor: Anchor | null;
  lessonId: number;
  chapterId: number;
  fallbackGloss?: string;
  onClose: () => void;
  explainerOpen: boolean;
  setExplainerOpen: (v: boolean) => void;
  aiExplainerEnabled: boolean;
}) {
  const info = selectedPhraseInfo$.get();
  const [translation, setTranslation] = useState<string | null>(null);
  const [phraseId, setPhraseId] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const { trackPhraseSaved } = useTrackFeedEvents();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!info) return;
    let cancelled = false;
    // PhrasePanel is reused across phrase selections, so reset the per-selection
    // state up front — otherwise a new phrase keeps the PREVIOUS phrase's
    // translation / phraseId / saved (save would target the wrong phrase, and an
    // error/timeout would show the old translation instead of this gloss).
    setLoading(true);
    setTranslation(null);
    setPhraseId(null);
    setSaved(false);
    // Never spin on "Parroting…" forever: if the translate stalls (e.g. the LLM
    // is down/slow), give up after this window and fall back to the word-by-word
    // gloss (save stays disabled — there's no server phrase to bookmark). A real
    // translate returns in ~1-3s, so this is just the "something's wrong" ceiling.
    const failTimer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 6000);
    callWithAuth(postPhrase, {
      startVideoWordId: info.startVideoWordId,
      endVideoWordId: info.endVideoWordId,
      testingString: info.text,
      lessonId,
    })
      .then((res) => {
        if (cancelled || !res) return;
        setTranslation(res.phrase);
        setPhraseId(res.phraseId);
        setSaved(res.isPhraseSaved);
      })
      .catch(() => {
        /* keep the local gloss fallback */
      })
      .finally(() => {
        if (!cancelled) {
          clearTimeout(failTimer);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(failTimer);
    };
  }, [info, lessonId]);

  const toggleSaved = () => {
    if (phraseId == null) return;
    const next = !saved;
    setSaved(next); // optimistic
    trackPhraseSaved(next);
    callWithAuth(putPhraseSaved, {
      phraseId,
      lessonId,
      isPhraseSaved: next,
    })
      .then(() => {
        // Only confirm the toast AFTER a successful SAVE (not unsave) — showing
        // it optimistically would falsely claim success if the request failed.
        // Fired via savedPhraseToast$ so the app-level <SavedPhraseToast> owns it
        // and it survives this popup unmounting on close.
        if (next) savedPhraseToast$.set((v) => v + 1);
        // Refresh the Dictionary's saved-phrase lists so the new (or removed)
        // phrase shows without a reload. Keys match DictionaryPage's
        // KNOWN/LEARNING_PHRASES_QUERY_KEY (iOS invalidatePhrasesDataProvider).
        // refetchType:"all" is REQUIRED: the global query config sets
        // refetchOnMount:false, so a plain invalidate would only mark these
        // (inactive) queries stale and they'd never refetch when the Dictionary
        // opens — forcing the refetch now keeps the cache fresh.
        queryClient.invalidateQueries({
          queryKey: ["learningPhrases"],
          refetchType: "all",
        });
        queryClient.invalidateQueries({
          queryKey: ["knownPhrases"],
          refetchType: "all",
        });
      })
      .catch(() => setSaved(!next));
  };

  const shownText = translation ?? fallbackGloss ?? "";
  // "Parroting… 🦜" for the WHOLE translate (iOS parity), not the gloss. Once the
  // call finishes we show the LLM translation, or fall back to the word-by-word
  // gloss only if it failed (no phraseId → save stays disabled).
  const isParroting = loading;

  return (
    <>
      <FloatingBubble
        anchor={anchor}
        onClose={onClose}
        showBackdrop={!explainerOpen}
      >
        <span
          className="leading-snug min-w-0"
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: TEXT_COLOR,
            overflowWrap: "anywhere",
          }}
        >
          {isParroting ? "Parroting… 🦜" : shownText}
        </span>
        <button
          type="button"
          aria-label={saved ? "Remove saved phrase" : "Save phrase"}
          onClick={(e) => {
            e.stopPropagation();
            toggleSaved();
          }}
          disabled={phraseId == null}
          className="shrink-0 flex items-center justify-center disabled:opacity-40"
          style={{ padding: "0 2px" }}
        >
          <Bookmark
            size={ICON_SIZE}
            color={saved ? colors.green : ICON_DARK}
            fill={saved ? colors.green : "none"}
            strokeWidth={2}
          />
        </button>
        {aiExplainerEnabled && (
          <ExplainButton onClick={() => setExplainerOpen(true)} />
        )}
      </FloatingBubble>

      {aiExplainerEnabled && explainerOpen && info && (
        <Explainer
          word={info.text}
          translation={shownText}
          context={info.text}
          isPhrase
          lessonId={lessonId}
          chapterId={chapterId}
          onClose={() => setExplainerOpen(false)}
        />
      )}
    </>
  );
});

export { BottomPanel };
